import React from 'react';
import { isAbortError, isBenignExternalScript } from '../../utils/abortError';
import logError from '../../utils/logError';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    if (isAbortError(error)) return null;
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('🚨 Frontend Error:', error);
    console.error('Component Stack:', info.componentStack);
    logError(error?.message || String(error), error?.stack || info?.componentStack || '', window.location.href);
  }

  componentDidMount() {
    this._onerror = (msg, source, lineno, colno, error) => {
      if (isAbortError(error) || isBenignExternalScript(source, error?.stack)) return;
      logError(msg || String(error?.message || ''), error?.stack || '', source || window.location.href);
    };
    this._onrejection = (event) => {
      const err = event.reason;
      if (isAbortError(err) || isBenignExternalScript('', err?.stack)) {
        event.preventDefault();
        return;
      }
      logError(err?.message || String(err || 'Unhandled rejection'), err?.stack || '', window.location.href);
    };
    window.addEventListener('error', this._onerror);
    window.addEventListener('unhandledrejection', this._onrejection);
  }

  componentWillUnmount() {
    window.removeEventListener('error', this._onerror);
    window.removeEventListener('unhandledrejection', this._onrejection);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={styles.icon}>⚠️</div>
            <h2 style={styles.title}>حدث خطأ غير متوقع</h2>
            <p style={styles.text}>حدث خطأ أثناء تحميل الصفحة. يرجى المحاولة مرة أخرى.</p>
            <button style={styles.button} onClick={() => window.location.href = '/login'}>
              العودة إلى صفحة تسجيل الدخول
            </button>
            <details style={styles.details}>
              <summary style={styles.summary}>تفاصيل الخطأ</summary>
              <pre style={styles.pre}>{this.state.error?.message}</pre>
            </details>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const styles = {
  container: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: 'var(--color-bg)', direction: 'rtl' },
  card: { backgroundColor: 'var(--color-white)', padding: 'var(--sp-4xl)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', textAlign: 'center', maxWidth: '400px' },
  icon: { fontSize: '48px', marginBottom: 'var(--sp-lg)' },
  title: { fontSize: 'var(--font-size-lg)', color: 'var(--color-danger-text)', margin: '0 0 12px 0' },
  text: { fontSize: 'var(--font-size-base)', color: 'var(--color-text-secondary)', marginBottom: 'var(--sp-2xl)' },
  button: { backgroundColor: 'var(--color-primary)', color: 'var(--color-white)', padding: '10px 20px', border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: 'var(--font-size-base)' },
  details: { marginTop: 'var(--sp-lg)', textAlign: 'right' },
  summary: { cursor: 'pointer', color: 'var(--color-primary)', fontSize: 'var(--font-size-sm)' },
  pre: { backgroundColor: '#f0f0f0', padding: 'var(--sp-md)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-xs)', marginTop: 'var(--sp-sm)', overflowX: 'auto' },
};
